/**
 * Route Parameter Types
 *
 * This file defines TypeScript interfaces for route parameters used throughout the application.
 * These types ensure consistency and type safety when accessing URL parameters via useParams().
 *
 * NAMING CONVENTION NOTE:
 * Route parameters use snake_case (e.g., report_unique_code) to maintain consistency with:
 * - Backend API response field names
 * - Database column naming conventions
 * - The ReportConfiguration data model
 *
 * While React Router typically uses camelCase for route params, we've chosen to align with
 * the backend data model to avoid confusion and reduce the need for field name transformations.
 */

/**
 * Route parameters for report-related routes
 *
 * Used in:
 * - /reports/:report_unique_code (ReportViewPage)
 * - /admin/reports-configuration/edit/:report_unique_code (ReportConfigurationForm)
 *
 * Note: Extends Record<string, string | undefined> to satisfy React Router's useParams constraint
 */
export interface ReportRouteParams extends Record<string, string | undefined> {
  /**
   * The unique identifier for a report configuration.
   * Format: Uppercase letters and underscores only (e.g., "MONTHLY_REVENUE_REPORT")
   *
   * Note: Uses snake_case to match the backend data model field name.
   */
  report_unique_code: string;
}

/**
 * Route parameters for product hierarchy routes (Manage Programs)
 *
 * Used in:
 * - /manage-programs/product-code/:productCode (ProductLineContainer)
 * - /manage-programs/product-code/:productCode/rpn/:rpnId (RpnContainer)
 * - /manage-programs/product-code/:productCode/rpn/:rpnId/fpn/:fpnId (FpnContainer)
 *
 * URL Format:
 * - Product Code: The actual product code (e.g., 'ABC123')
 * - RPN ID: Prefixed with 'R' (e.g., 'R456' for rpnId 456)
 * - FPN ID: Prefixed with 'F' + 6-digit zero-padded number (e.g., 'F010652' for fpnId 10652)
 *
 * Note: Extends Record<string, string | undefined> to satisfy React Router's useParams constraint
 */
export interface ProductLineRouteParams extends Record<string, string | undefined> {
  /**
   * The product code (e.g., 'ABC123').
   * This is the actual product code, not the product line ID.
   */
  productCode: string;
}

export interface RpnRouteParams extends ProductLineRouteParams {
  /**
   * The RPN (Regional Product Name) identifier.
   * Format in URL: Prefixed with 'R' (e.g., 'R456').
   * Use extractRpnId() from idFormattingUtils to get the raw numeric ID.
   */
  rpnId: string;
}

export interface FpnRouteParams extends RpnRouteParams {
  /**
   * The FPN (Financial Product Name) identifier.
   * Format in URL: Prefixed with 'F' + 6-digit zero-padded number (e.g., 'F010652').
   * Use extractFpnId() from idFormattingUtils to get the raw numeric ID.
   */
  fpnId: string;
}

/**
 * Route parameters for access management routes
 *
 * Used in:
 * - /access-management/edit/:access_id (AccessManagementForm)
 *
 * Note: Extends Record<string, string | undefined> to satisfy React Router's useParams constraint
 */
export interface AccessManagementRouteParams extends Record<string, string | undefined> {
  /**
   * The unique identifier for a user access record.
   */
  access_id?: string;
}

/**
 * Route parameter keys as constants for type-safe route path construction
 */
export const ROUTE_PARAM_KEYS = {
  REPORT_UNIQUE_CODE: 'report_unique_code',
  PRODUCT_CODE: 'productCode',
  RPN_ID: 'rpnId',
  FPN_ID: 'fpnId',
  ACCESS_ID: 'access_id',
} as const;

/**
 * Type for route parameter keys
 */
export type RouteParamKey = (typeof ROUTE_PARAM_KEYS)[keyof typeof ROUTE_PARAM_KEYS];
